import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { updateSessionEngagement } from './useCommunitySessionsHook';
import type {
  LikeResponse,
  SessionLikesResponse,
  CommentResponse,
  CommentsListResponse,
} from '@/types/social';

// Query keys for cache management
export const communitySocialKeys = {
  all: ['community-social'] as const,
  likes: (sessionId: string) => [...communitySocialKeys.all, 'likes', sessionId] as const,
  comments: (sessionId: string) => [...communitySocialKeys.all, 'comments', sessionId] as const,
};

/**
 * Hook for managing likes on a community session
 */
export const useSessionLike = (sessionId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const { data: likesData, isLoading } = useQuery({
    queryKey: communitySocialKeys.likes(sessionId),
    queryFn: () => apiClient.get<SessionLikesResponse>(
      `/community-social/sessions/${sessionId}/likes`
    ),
    enabled: !!sessionId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const toggleLikeMutation = useMutation({
    mutationFn: () => apiClient.post<LikeResponse>(
      `/community-social/sessions/${sessionId}/like`,
      {}
    ),
    // Optimistic update before API call for instant feedback
    onMutate: async () => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: communitySocialKeys.likes(sessionId) });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueryData<SessionLikesResponse>(
        communitySocialKeys.likes(sessionId)
      );

      // Optimistically update to the new value
      if (previousData) {
        const isCurrentlyLiked = previousData.liked_by_current_user;
        queryClient.setQueryData(
          communitySocialKeys.likes(sessionId),
          {
            ...previousData,
            like_count: isCurrentlyLiked
              ? previousData.like_count - 1
              : previousData.like_count + 1,
            liked_by_current_user: !isCurrentlyLiked,
          }
        );
      }

      // Return context with the previous value for rollback
      return { previousData };
    },
    onSuccess: (data) => {
      // Update with actual server response (corrects any optimistic mismatch)
      queryClient.setQueryData(
        communitySocialKeys.likes(sessionId),
        (old: SessionLikesResponse | undefined) => ({
          ...old,
          like_count: data.like_count,
          liked_by_current_user: data.liked,
          likes: old?.likes ?? [],
        })
      );
      // Sync like count to community sessions list cache
      updateSessionEngagement(queryClient, sessionId, { like_count: data.like_count });
    },
    onError: (_err, _variables, context) => {
      // Rollback to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(
          communitySocialKeys.likes(sessionId),
          context.previousData
        );
      }
      toast({
        title: 'Error',
        description: 'Failed to update like. Please try again.',
        variant: 'destructive',
      });
    },
  });

  return {
    likeCount: likesData?.like_count ?? 0,
    isLiked: likesData?.liked_by_current_user ?? false,
    likes: likesData?.likes ?? [],
    isLoading,
    toggleLike: () => {
      if (!user) {
        toast({
          title: 'Sign in required',
          description: 'Please sign in to like sessions.',
          variant: 'default',
        });
        return;
      }
      toggleLikeMutation.mutate();
    },
    isToggling: toggleLikeMutation.isPending,
    canLike: !!user,
  };
};

/**
 * Hook for managing comments on a community session
 */
export const useSessionComments = (sessionId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [page, setPage] = useState(1);

  const { data: commentsData, isLoading, refetch } = useQuery({
    queryKey: [...communitySocialKeys.comments(sessionId), page],
    queryFn: () => apiClient.get<CommentsListResponse>(
      `/community-social/sessions/${sessionId}/comments?page=${page}&limit=20`
    ),
    enabled: !!sessionId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const createCommentMutation = useMutation({
    mutationFn: (content: string) => apiClient.post<CommentResponse>(
      `/community-social/sessions/${sessionId}/comments`,
      { content }
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communitySocialKeys.comments(sessionId) });
      toast({
        title: 'Comment posted',
        description: 'Your comment is being reviewed and will appear shortly.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to post comment. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      apiClient.patch<CommentResponse>(
        `/community-social/comments/${commentId}`,
        { content }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communitySocialKeys.comments(sessionId) });
      toast({
        title: 'Comment updated',
        description: 'Your comment has been updated and is being reviewed.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update comment. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) =>
      apiClient.delete(`/community-social/comments/${commentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communitySocialKeys.comments(sessionId) });
      toast({
        title: 'Comment deleted',
        description: 'Your comment has been removed.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete comment. Please try again.',
        variant: 'destructive',
      });
    },
  });

  return {
    comments: commentsData?.comments ?? [],
    totalCount: commentsData?.total_count ?? 0,
    hasMore: commentsData?.has_more ?? false,
    isLoading,
    page,
    setPage,
    refetch,
    createComment: (content: string) => {
      if (!user) {
        toast({
          title: 'Sign in required',
          description: 'Please sign in to post comments.',
          variant: 'default',
        });
        return;
      }
      createCommentMutation.mutate(content);
    },
    isCreating: createCommentMutation.isPending,
    updateComment: (commentId: string, content: string) => {
      updateCommentMutation.mutate({ commentId, content });
    },
    isUpdating: updateCommentMutation.isPending,
    deleteComment: (commentId: string) => {
      deleteCommentMutation.mutate(commentId);
    },
    isDeleting: deleteCommentMutation.isPending,
    canComment: !!user,
  };
};
